import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import ResetFilters from "../../../components/filters/ResetFilters";
import { useTranslation } from "react-i18next";
import FilterShiftReports from "../../../api/shift-report/filterShiftReports";
import SelectFilter from "../../../components/filters/SelectFilter";
import SearchFilter from "../../../components/filters/SearchFilter";
import ExportDataButton from "../../../components/ExportDataButton";
import { useLocation } from "react-router-dom";
import Chip from "../../../components/Chip";
import { formatDate } from "../../../utils/formatDate";
import { downloadFile } from "../../../api/downloadFile";
import { ExportSellOrdersShiftReport } from "../../../api/sell-orders/exportSellOrdersShiftReport";
import OfficesFilter from "../../../components/filters/OfficesFilter";
import CompanyFilter from "../../../components/filters/CompanyFilter";
import { CentsToDollars } from "../../../utils/formatPrice";
import { useGlobalContext } from "../../../contexts/GlobalContext";

function Index(props) {
  const { t } = useTranslation();

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Shift Report")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const { data, errorMessage, loadingData, pageCount } =
    useData(FilterShiftReports);

  const getDefaultDateRange = () => {
    const today = new Date();
    const yesterday = new Date(today);

    // Set yesterday to 00:00
    yesterday.setDate(today.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);

    // Set today to end of day (23:59:59)
    today.setHours(23, 59, 59, 999);

    // Format the dates to "YYYY/MM/DD"
    const formatDate = (date) =>
      `${date.getFullYear()}/${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}`;

    return `${formatDate(yesterday)} - ${formatDate(today)}`;
  };

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  const handleExportCSV = async () => {
    try {
      const response = await FilterShiftReports(-1, queryParams);
      const ids = response.data.map((item) => item.id);
      await downloadFile(ExportSellOrdersShiftReport, { ids: ids }, "shift_reports.csv");
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <Layout title={t("Shift Report")} breadcrumbs={t("Card Issuing")}>
      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "id",
                    label: t("By ID"),
                  },
                  {
                    value: "user",
                    label: t("By user"),
                  },
                ]}
                defaultValue="id"
                placeholder={<div>{t("Search by")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter defaultValue={getDefaultDateRange()} />
            </FilterContextProvider>
            <FilterContextProvider name="office_id">
              <OfficesFilter />
            </FilterContextProvider>
            <FilterContextProvider name="corporate_customer_id">
              <CompanyFilter />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            <ExportDataButton
              className="btn btn-secondary btn-medium"
              onClick={handleExportCSV}
              label={t("Export CSV")}
              disabled={!data.data?.length}
            />
          </>
        }
        columns={[
          { header: t("Order Number"), accessor: "id" },
          { header: t("Date"), accessor: "created_at" },
          { header: t("Order Type"), accessor: "type" },
          { header: t("Order Status"), accessor: "status" },
          { header: t("User"), accessor: "created_by_name" },
          { header: t("User ID"), accessor: "created_by" },
          { header: t("Office"), accessor: "office_name" },
          { header: t("Total Purchase Amount"), accessor: "total_cost", headerClassName: "text-right" },
          { header: t("Tax - Items"), accessor: "specialty_items_tax", headerClassName: "text-right" },
          { header: t("Tax - Fee"), accessor: "service_fee_tax", headerClassName: "text-right" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        customOutputs={(columnName, value, row) => {
          switch (columnName) {
            case "status":
              let status = "inactive";
              if (
                value === "submitted" ||
                value === "success" ||
                value === "completed"
              ) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "specialty_items_tax":
            case "service_fee_tax":
              return <p className="text-right">{CentsToDollars(value ?? 0)}</p>;
            case "total_cost":
              if (
                row?.type?.toUpperCase() === "VOID" ||
                row?.status?.toUpperCase()?.includes("VOID")
              ) {
                value *= -1;
              }
              return <p className="text-right">{CentsToDollars(value)}</p>;
            case "created_at":
              return formatDate(value);
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
